import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
  HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
  HOUSEHOLD_APPLICATION_STATUS_STARTED,
  HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  HOUSEHOLD_APPLICATION_STATUS_PAID,
  HOUSEHOLD_APPLICATION_STATUS_DELIVERED,
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,

  HOUSEHOLD_LISTING_STATUS_ACTIVE,
  HOUSEHOLD_LISTING_STATUS_ARCHIVED,
} from 'common/householdConstants';

import {
  DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN,
} from 'common/dataTableConstants';

import {
  getNextQualifiedHousehold,
  getPendingHouseholds,
} from 'common/householdApplication/prequalificationHelpers';

import {
  actionDataTableLoadItems,
} from 'client/helpers/dataTables';

import useDispatch from 'client/hooks/useDispatch';

import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrHouseholdKanbanBoardCard from 'client/components/SrHouseholdKanbanBoardCard';
import SrRenterHouseholdsModal from 'client/components/SrRenterHouseholdsModal';

const householdColumnTitleMap = {
  [HOUSEHOLD_APPLICATION_STATUS_REQUESTED]: 'wants to apply',
  [HOUSEHOLD_APPLICATION_STATUS_STARTED]: 'started',
  [HOUSEHOLD_APPLICATION_STATUS_COMPLETED]: 'completed',
  [HOUSEHOLD_APPLICATION_STATUS_PAID]: 'paid',
  [HOUSEHOLD_APPLICATION_STATUS_DELIVERED]: 'delivered',
  [HOUSEHOLD_APPLICATION_STATUS_ACCEPTED]: 'accepted',
  [HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED]: 'signed',
};

export function getDataTableSettingsFromProps({
  moveInDateRanges,
  listingManagementCompany,
  listingServices,
  leasingManagerUserId,
  householdApplicationStatus,
  excludeHouseholdApplicationStatusList,
  includeHouseholdApplicationStatusList,
  householdListingStatus,
}) {
  const dataTableSettings = {
    itemsPropName: 'households',
    dataSource: DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN,
    rootViewId: householdApplicationStatus + householdListingStatus,
    count: 20,
    initialViewSettings: {
      includeItemsCount: true,
      sort: {},
      filter: {},
      customParams: {
        householdKanbanApplicationColumn: {
          moveInDateRanges,
          listingManagementCompany,
          listingServices,
          leasingManagerUserId,
          excludeHouseholdApplicationStatusList,
          includeHouseholdApplicationStatusList,
          householdApplicationStatus,
          householdListingStatus,
        },
      },
    },
  };

  if (householdListingStatus === HOUSEHOLD_LISTING_STATUS_ACTIVE) {
    dataTableSettings.initialViewSettings.loadAllData = true;
  }

  return dataTableSettings;
}

export function sortHouseholds({ households }) {
  households.sort((householdA, householdB) => {
    const householdATs = new Date(householdA.leadRenter.frontLastMessageReceivedDate).getTime();
    const householdBTs = new Date(householdB.leadRenter.frontLastMessageReceivedDate).getTime();
    if (householdATs > householdBTs) {
      return -1;
    }
    if (householdATs < householdBTs) {
      return 1;
    }
    return 0;
  });

  return {
    activeHouseholds: households,
    backupHouseholds: [],
  };
}

export function sortPaidHouseholds({ households }) {
  const householdsByListingId = households.reduce((memo, household) => {
    const listingId = household.listingApplications[0].listing.id;
    if (!memo[listingId]) {
      memo[listingId] = [];
    }
    memo[listingId].push(household);
    return memo;
  }, {});

  const activeHouseholdIds = [];

  for (const listingId of Object.keys(householdsByListingId)) {
    const householdByListing = householdsByListingId[listingId];

    const nextQualifiedHousehold = getNextQualifiedHousehold({
      households: householdByListing.map((household) => ({
        id: household.id,
        moveInDate: new Date(household.renterMoveInDate),
        listing: household.listingApplications[0].listing,
        tenantScreeningCriteria: {
          ...household.listingApplications[0].tenantScreeningCriteria,
          breedRestrictions: household.listingApplications[0].breedRestrictions,
        },
        renters: household.users,
        householdApplications: household.listingApplications[0].applications,
        householdGuarantors: household.householdGuarantors,
        guarantorApplications: household.guarantorApplications,
        tenantAlertReports: household.tenantAlertReports,
        leadRenterId: household.leadRenterId,
      })),
    });
    if (nextQualifiedHousehold) {
      activeHouseholdIds.push(nextQualifiedHousehold.id);
      continue;
    }

    const tentativelyQualified = getPendingHouseholds({
      households: householdByListing.map((household) => ({
        id: household.id,
        moveInDate: new Date(household.renterMoveInDate),
        listing: household.listingApplications[0].listing,
        tenantScreeningCriteria: {
          ...household.listingApplications[0].tenantScreeningCriteria,
          breedRestrictions: household.listingApplications[0].breedRestrictions,
        },
        renters: household.users,
        householdApplications: household.listingApplications[0].applications,
        householdGuarantors: household.householdGuarantors,
        guarantorApplications: household.guarantorApplications,
        tenantAlertReports: household.tenantAlertReports,
        leadRenterId: household.leadRenterId,
      })),
    }).find((household) => household.householdQualification.isQualified);
    if (tentativelyQualified) {
      activeHouseholdIds.push(tentativelyQualified.id);
      continue;
    }
  }

  const { activeHouseholds, backupHouseholds } = households.reduce((memo, household) => {
    if (activeHouseholdIds.includes(household.id)) {
      memo.activeHouseholds.push(household);
    }
    else {
      memo.backupHouseholds.push(household);
    }

    return memo;
  }, {
    activeHouseholds: [],
    backupHouseholds: [],
  });

  activeHouseholds.sort((householdA, householdB) => {
    return (
      moment(householdA.leadRenter.frontLastMessageReceivedDate).unix() - moment(householdB.leadRenter.frontLastMessageReceivedDate).unix() // eslint-disable-line max-len
    );
  });

  return {
    activeHouseholds,
    backupHouseholds,
  };
}

const SrHouseholdKanbanBoardApplicationColumn = ({
  moveInDateRanges,
  listingManagementCompany,
  listingServices,
  leasingManagerUserId,
  householdApplicationStatus,
  excludeHouseholdApplicationStatusList,
  includeHouseholdApplicationStatusList,
}) => {

  const dispatch = useDispatch();
  const [showArchivedHouseholds, setShowArchivedHouseholds] = useState(false);
  const [showHouseholdRenterId, setShowHouseholdRenterId] = useState(null);

  const isLeaseSignedColumn = householdApplicationStatus === HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED;

  const reloadDataTableHousehold = (householdId) => {
    dispatch(actionDataTableLoadItems, {
      dataSource: DATA_TABLE_SOURCE_HOUSEHOLD_KANBAN_APPLICATION_COLUMN,
      viewId: `household-id-${householdId}`,
      filter: {
        'households.id': householdId,
      },
      count: 1,
    });
  };

  const activeHouseholdsDataTableSettings = getDataTableSettingsFromProps({
    moveInDateRanges,
    listingManagementCompany,
    listingServices,
    leasingManagerUserId,
    householdApplicationStatus,
    excludeHouseholdApplicationStatusList,
    includeHouseholdApplicationStatusList,
    householdListingStatus: HOUSEHOLD_LISTING_STATUS_ACTIVE,
  });

  const archivedHouseholdsDataTableSettings = getDataTableSettingsFromProps({
    moveInDateRanges,
    listingManagementCompany,
    listingServices,
    leasingManagerUserId,
    householdApplicationStatus,
    excludeHouseholdApplicationStatusList,
    includeHouseholdApplicationStatusList,
    householdListingStatus: HOUSEHOLD_LISTING_STATUS_ARCHIVED,
  });

  return (
    <div className='householdKanbanBoardColumn'>
      <SrDataTableConnected {...activeHouseholdsDataTableSettings}>
        {(activeHouseholdsDataTable) => {
          const activeHouseholdsDataTableDOM = (
            <div ref={activeHouseholdsDataTable.onInfiniteScrollWrapperRef}>
              {activeHouseholdsDataTable.households.map((household) => {
                return (
                  <SrHouseholdKanbanBoardCard
                    key={household.id}
                    household={household}
                    dispatch={dispatch}
                    householdApplicationStatus={householdApplicationStatus}
                    householdAdminsList={[]}
                    onClick={() => setShowHouseholdRenterId(household.leadRenterId)}
                  />
                );
              })}
            </div>
          );

          return (
            <SrDataTableConnected {...archivedHouseholdsDataTableSettings}>
              {(archivedHouseholdsDataTable) => {
                const inProgress = (
                  activeHouseholdsDataTable.dataTableApiState.inProgress
                  || archivedHouseholdsDataTable.dataTableApiState.inProgress
                );

                const allInProgress = (
                  activeHouseholdsDataTable.dataTableApiState.inProgress
                  && archivedHouseholdsDataTable.dataTableApiState.inProgress
                );

                const count = activeHouseholdsDataTable.householdsCount;

                const showArchivedHouseholdsText = (
                  !isLeaseSignedColumn
                  && archivedHouseholdsDataTable.householdsCount > 0
                  && activeHouseholdsDataTable.dataTableIsFullyLoaded
                );

                const ref = (archivedHouseholdsDataTable.householdsCount === null || showArchivedHouseholds)
                  ? archivedHouseholdsDataTable.onInfiniteScrollWrapperRef
                  : null;

                const archivedHouseholdsDataTableDOM = (
                  <div ref={ref}>
                    {showArchivedHouseholdsText && (
                      <div
                        className='archivedText'
                        onClick={() => setShowArchivedHouseholds(!showArchivedHouseholds)}
                      >
                        {showArchivedHouseholds
                          ? 'Archived'
                          : `View ${archivedHouseholdsDataTable.householdsCount} archived`
                        }
                      </div>
                    )}
                    {!showArchivedHouseholds
                      ? null
                      : archivedHouseholdsDataTable.households.map((household) => (
                        <SrHouseholdKanbanBoardCard
                          archived
                          key={household.id}
                          household={household}
                          dispatch={dispatch}
                          householdApplicationStatus={householdApplicationStatus}
                          householdAdminsList={[]}
                          onClick={() => setShowHouseholdRenterId(household.leadRenterId)}
                        />
                      ))}
                  </div>
                );

                return (
                  <>
                    <div className='columnName'>
                      {householdColumnTitleMap[householdApplicationStatus]}
                      {` ${allInProgress ? '' : `(${count})`}`}
                    </div>
                    <div className='columnCardContainer'>
                      {activeHouseholdsDataTableDOM}
                      {archivedHouseholdsDataTableDOM}
                      {inProgress && (
                        <div className='loading-ellipses' />
                      )}
                    </div>
                  </>
                );
              }}
            </SrDataTableConnected>
          );
        }}
      </SrDataTableConnected>

      {showHouseholdRenterId && (
        <SrRenterHouseholdsModal
          renter={{ id: showHouseholdRenterId }}
          onClose={() => setShowHouseholdRenterId(null)}
          householdAdminsList={[]}
          reloadDataTableHousehold={reloadDataTableHousehold}
        />
      )}
    </div>
  );
};

SrHouseholdKanbanBoardApplicationColumn.propTypes = {
  moveInDateRanges: PropTypes.array,
  listingManagementCompany: PropTypes.array,
  listingServices: PropTypes.array,
  accountManagerUserId: PropTypes.array,
  applicationStatus: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
};

SrHouseholdKanbanBoardApplicationColumn.displayName = 'SrHouseholdKanbanBoardApplicationColumn';

export default SrHouseholdKanbanBoardApplicationColumn;
